import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { api, setToken } from "@/lib/api";

const Ctx = createContext(null);

function extractSessionId(url) {
  if (!url) return null;
  const m = url.match(/[?#&]session_id=([^&#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const processed = useRef(new Set());

  const refresh = useCallback(async () => {
    try {
      const me = await api("/auth/me");
      setUser(me);
    } catch {
      setUser(null);
      setToken(null);
    }
  }, []);

  const processGoogleSession = useCallback(async (sid) => {
    if (!sid || processed.current.has(sid)) return false;
    processed.current.add(sid);
    try {
      const res = await api("/auth/session", {
        method: "POST",
        body: { session_id: sid },
        auth: false,
      });
      setToken(res.session_token);
      setUser(res.user);
      return true;
    } catch {
      processed.current.delete(sid);
      return false;
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const sid = extractSessionId(window.location.href);
        if (sid) {
          const ok = await processGoogleSession(sid);
          if (ok) {
            const cleanSearch = window.location.search
              .replace(/[?&]session_id=[^&#]*/, "")
              .replace(/^&/, "?");
            const cleanHash = window.location.hash.replace(/[#&]session_id=[^&#]*/, "");
            window.history.replaceState(
              window.history.state,
              "",
              `${window.location.pathname}${cleanSearch.startsWith("?") ? cleanSearch : ""}${cleanHash}`
            );
          } else {
            await refresh();
          }
        } else {
          await refresh();
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [processGoogleSession, refresh]);

  const signIn = useCallback(async (token, u) => {
    setToken(token);
    setUser(u);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch (e) {
      console.warn("logout request failed (clearing local session anyway)", e);
    }
    setToken(null);
    setUser(null);
  }, []);

  const loginWithGoogle = useCallback(() => {
    const redirect = encodeURIComponent(`${window.location.origin}/`);
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirect}`;
  }, []);

  const deleteAccount = useCallback(async () => {
    try {
      await api("/auth/account", { method: "DELETE" });
    } catch (e) {
      console.warn("delete account failed", e);
      return false;
    }
    setToken(null);
    setUser(null);
    return true;
  }, []);

  const updatePreferences = useCallback(async (home_layout) => {
    const updated = await api("/auth/preferences", { method: "PUT", body: { home_layout } });
    setUser(updated);
    return updated;
  }, []);

  const value = useMemo(
    () => ({ user, loading, signIn, signOut, refresh, loginWithGoogle, deleteAccount, updatePreferences }),
    [user, loading, signIn, signOut, refresh, loginWithGoogle, deleteAccount, updatePreferences]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
